import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteResource } from '../../api/production-request-api/index.js';
import { toast } from '@pella/sash-framework';
import { QUERY_KEYS } from '../../data/constants';
import { useAppContext } from '../../providers';

/**
 * Hook containing mutation function for deleting a Resource.
 */
export const useDeleteResource = () => {
  const queryClient = useQueryClient();
  const { selectedPlant } = useAppContext();
  const queryKey = [QUERY_KEYS.RESOURCE_DATA, selectedPlant, { resourceQueues: true }];

  return useMutation({
    /**
     * mutateFn - Function to call deleteResource.
     * @param {Pella.ProductionRequestAPI.Resource['id']} id - The ID of the Resource to delete.
     */
    mutationFn: (id) => deleteResource(id),
    onMutate: (id, context) => {
      // Snapshot the previous value
      const previousResources = context.client.getQueryData(queryKey);

      // Optimistically update the resource data in the cache
      queryClient.setQueryData(queryKey, (/** @type {Pella.ProductionRequestAPI.Resource[]} */ oldData) => {
        // Guard against undefined data - if no data in cache, return empty array or previous data
        if (!oldData || !Array.isArray(oldData)) {
          return previousResources || [];
        }
        return oldData.map((resource) => resource.id === id);
      });

      // Return a result with the snapshotted value
      return { previousResources };
    },
    onError: (_err, _variables, onMutateResult, context) => {
      // Rollback to the previous value on error
      context.client.setQueryData(queryKey, onMutateResult?.previousResources);
    },
    onSuccess: () => {
      toast.success('Resource deleted successfully!');

      // Invalidate queries to refetch the updated data
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RESOURCE_DATA, selectedPlant] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUEUE_DATA, selectedPlant] });
    },
  });
};
